export const VIDEO_PROGRESS_ENDPOINT = '/api/v2/video_progress'
export const VIDEO_SEGMENT_COUNT = 100
export const VIDEO_SEGMENT_HEX_LENGTH = 25
export const VIDEO_FLUSH_MIN_MS = 60_000
export const VIDEO_FLUSH_JITTER_MS = 60_000
const MAX_CONTIGUOUS_POSITION_DELTA = 3

export interface VideoProgressPayload {
  session_uuid: string
  course_id: number
  lesson_id: number
  sequence: number
  duration_seconds: number
  watched_segments: string
  play_seconds: number
  last_position_seconds: number
  last_segment?: number
  client_started_at: string
  ended?: boolean
}

export type VideoProgressTransport = (payload: VideoProgressPayload) => Promise<void>
export type VideoProgressBeacon = (payload: VideoProgressPayload) => void

export interface PlaybackPosition {
  seconds: number
  duration: number
}

export class VideoSegmentTracker {
  private readonly bits = Array<boolean>(VIDEO_SEGMENT_COUNT).fill(false)
  private playing = false
  private previousPosition: number | null = null
  private playSecondsValue = 0
  private lastPositionValue = 0
  private lastPlayedSegmentValue: number | undefined

  setPlaying(playing: boolean): void {
    this.playing = playing
    if (!playing) this.previousPosition = null
  }

  seeked(position: PlaybackPosition): void {
    this.lastPositionValue = Math.max(0, position.seconds)
    this.previousPosition = this.lastPositionValue
  }

  update(position: PlaybackPosition): boolean {
    const seconds = Math.max(0, position.seconds)
    const duration = position.duration
    const previous = this.previousPosition
    this.lastPositionValue = seconds
    this.previousPosition = seconds

    if (!this.playing || !Number.isFinite(duration) || duration <= 0 || previous === null) return false

    const delta = seconds - previous
    if (delta <= 0 || delta > MAX_CONTIGUOUS_POSITION_DELTA) return false

    this.playSecondsValue += delta
    const first = this.segmentFor(previous, duration)
    const last = this.segmentFor(Math.max(previous, seconds - 0.000001), duration)
    for (let segment = first; segment <= last; segment += 1) {
      this.bits[segment] = true
    }
    this.lastPlayedSegmentValue = last
    return true
  }

  get watchedSegments(): string {
    const chars: string[] = []
    for (let group = VIDEO_SEGMENT_HEX_LENGTH - 1; group >= 0; group -= 1) {
      let value = 0
      for (let bit = 0; bit < 4; bit += 1) {
        if (this.bits[group * 4 + bit]) value += 1 << bit
      }
      chars.push(value.toString(16))
    }
    return chars.join('')
  }

  get playSeconds(): number {
    return Math.floor(this.playSecondsValue)
  }

  get lastPositionSeconds(): number {
    return Math.floor(this.lastPositionValue)
  }

  get lastPlayedSegment(): number | undefined {
    return this.lastPlayedSegmentValue
  }

  get watchedSegmentCount(): number {
    return this.bits.filter(Boolean).length
  }

  private segmentFor(seconds: number, duration: number): number {
    return Math.min(VIDEO_SEGMENT_COUNT - 1, Math.max(0, Math.floor(seconds / duration * VIDEO_SEGMENT_COUNT)))
  }
}

interface VideoEngagementReporterOptions {
  courseId: number
  lessonId: number
  transport?: VideoProgressTransport
  beacon?: VideoProgressBeacon
  uuid?: () => string
  wallNow?: () => number
  random?: () => number
}

export class VideoEngagementReporter {
  private readonly courseId: number
  private readonly lessonId: number
  private readonly transport: VideoProgressTransport
  private readonly beacon: VideoProgressBeacon
  private readonly wallNow: () => number
  private readonly random: () => number
  private readonly tracker = new VideoSegmentTracker()
  private readonly sessionUuid: string
  private readonly clientStartedAt: string
  private durationSeconds = 0
  private sequence = 0
  private dirty = false
  private timer: number | null = null
  private disposed = false

  constructor(options: VideoEngagementReporterOptions) {
    this.courseId = options.courseId
    this.lessonId = options.lessonId
    this.transport = options.transport ?? defaultVideoProgressTransport
    this.beacon = options.beacon ?? defaultVideoProgressBeacon
    this.wallNow = options.wallNow ?? (() => Date.now())
    this.random = options.random ?? Math.random
    this.sessionUuid = (options.uuid ?? (() => crypto.randomUUID()))()
    this.clientStartedAt = new Date(this.wallNow()).toISOString()
  }

  play(): void {
    this.tracker.setPlaying(true)
    this.scheduleFlush()
  }

  pause(position?: PlaybackPosition): void {
    if (position) this.position(position)
    this.tracker.setPlaying(false)
    void this.flush()
  }

  seeked(position: PlaybackPosition): void {
    this.durationSeconds = Math.max(this.durationSeconds, Math.floor(position.duration))
    this.tracker.seeked(position)
  }

  position(position: PlaybackPosition): void {
    this.durationSeconds = Math.max(this.durationSeconds, Math.floor(position.duration))
    this.dirty = this.tracker.update(position) || this.dirty
  }

  ended(position?: PlaybackPosition): void {
    if (position) this.position(position)
    this.tracker.setPlaying(false)
    void this.flush(true)
  }

  async flush(ended = false): Promise<void> {
    const hasPlayback = this.tracker.playSeconds > 0 || this.tracker.watchedSegmentCount > 0
    if (this.disposed || !hasPlayback || (!this.dirty && !ended) || this.durationSeconds <= 0) return

    const payload = this.payload(ended)
    this.dirty = false
    try {
      await this.transport(payload)
    } catch {
      this.dirty = true
    }
  }

  pagehide(ended = false): void {
    if (!this.dirty || this.durationSeconds <= 0) return
    this.beacon(this.payload(ended))
    this.dirty = false
  }

  dispose(): void {
    this.pagehide(true)
    this.disposed = true
    if (this.timer !== null) window.clearTimeout(this.timer)
    this.timer = null
  }

  private payload(ended: boolean): VideoProgressPayload {
    this.sequence += 1
    return {
      session_uuid: this.sessionUuid,
      course_id: this.courseId,
      lesson_id: this.lessonId,
      sequence: this.sequence,
      duration_seconds: this.durationSeconds,
      watched_segments: this.tracker.watchedSegments,
      play_seconds: this.tracker.playSeconds,
      last_position_seconds: this.tracker.lastPositionSeconds,
      ...(this.tracker.lastPlayedSegment === undefined ? {} : { last_segment: this.tracker.lastPlayedSegment }),
      client_started_at: this.clientStartedAt,
      ...(ended ? { ended: true } : {})
    }
  }

  private scheduleFlush(): void {
    if (this.timer !== null || this.disposed) return
    const delay = VIDEO_FLUSH_MIN_MS + Math.floor(this.random() * VIDEO_FLUSH_JITTER_MS)
    this.timer = window.setTimeout(async () => {
      this.timer = null
      await this.flush()
      this.scheduleFlush()
    }, delay)
  }
}

export const defaultVideoProgressTransport: VideoProgressTransport = async (payload) => {
  const response = await fetch(VIDEO_PROGRESS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    keepalive: true,
    body: JSON.stringify(payload)
  })
  if (!response.ok) throw new Error(`video progress failed: ${response.status}`)
}

export const defaultVideoProgressBeacon: VideoProgressBeacon = (payload) => {
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return
  const body = new Blob([JSON.stringify(payload)], { type: 'application/json' })
  navigator.sendBeacon(VIDEO_PROGRESS_ENDPOINT, body)
}
